import { useMemo } from 'react';
import { Building2, LandPlot, ListChecks, MapPin, Sprout } from 'lucide-react';
import { PageHeader } from '../ui/PageHeader';
import { KpiCard } from '../ui/KpiCard';
import { Card, Grid, Stack } from '../ui/Card';
import { Disclosure } from '../ui/Disclosure';
import { EmptyState } from '../ui/EmptyState';
import { EvidenceMark } from '../ui/EvidenceMark';
import { DataGate } from './parts';
import { usePageTrail } from '../shell/ShellContext';
import { useRouteSource } from '../data-source/provider';
import { OverviewApi } from './OverviewApi';
import { useTrackData } from '../data/useTrackData';
import { FILES } from '../data/loader';
import {
  countOf,
  cycleTitle,
  dateLong,
  ha,
  haUnit,
  m3,
  num,
  pct,
  WORDS,
} from '../lib/format';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { COMMON } from '../../i18n/common';
import { tr } from '../../i18n/runtime';
import { DENSITY } from '../lib/levels';
import type {
  AttentionItem,
  ContourMeta,
  Crop,
  Cycle,
  Massif,
  Meta,
  QuarantineFile,
  WaterFile,
  Work,
} from '../data/types';
import {
  ActiveCycle,
  closedCycleFacts,
  ClosedCycleRow,
  CropsBreakdown,
  levelCaption,
  MapFrameCard,
  MassifTable,
  QueueItem,
  ReadyRow,
  type CropSlice,
  type QueueItemView,
} from './overviewParts';
import './pages.css';
import './overview.css';
import { srv } from '../../i18n/serverText';

/* ------------------------------------------------------------- тексты */

const m = defineMessages({
  districtName: { id: 'district.overview.districtName', defaultMessage: 'Бука', description: 'Название района по умолчанию (Бука), если источник данных его не назвал; географическое имя — транслитерация' },
  title: { id: 'district.overview.title', defaultMessage: 'Район {district}', description: 'Заголовок страницы «Район» (вариант на пробных данных); {district} — название района' },
  purposeDate: {
    id: 'district.overview.purposeDate',
    defaultMessage: 'Что происходит на земле района на дату среза {date}. Всё считается в массивах, контурах, хозяйствах и гектарах района — доли других уровней сюда не заходят.',
    description: 'Подзаголовок страницы «Район»; {date} — полная дата среза («26 августа 2026»)',
  },
  purposeNoDate: {
    id: 'district.overview.purposeNoDate',
    defaultMessage: 'Что происходит на земле района на дату среза. Всё считается в массивах, контурах, хозяйствах и гектарах района — доли других уровней сюда не заходят.',
    description: 'Подзаголовок страницы «Район», когда дата среза неизвестна',
  },
  markStub: { id: 'district.overview.mark.stub', defaultMessage: 'Заглушка вместо трека', description: 'Метка доверия справа в заголовке: данные — заглушка вместо готового набора' },
  markTrackB: { id: 'district.overview.mark.trackB', defaultMessage: 'Трек B — реальная геометрия, слои синтезированы', description: 'Метка доверия справа в заголовке: набор данных «трек B» — реальные контуры, остальные слои синтетические' },
  markTrackA: { id: 'district.overview.mark.trackA', defaultMessage: 'Трек A — весь срез сгенерирован', description: 'Метка доверия справа в заголовке: набор данных «трек A» — всё сгенерировано' },

  kpiActive: { id: 'district.overview.kpi.active', defaultMessage: 'Активный агроцикл', description: 'Название главного KPI: площадь идущего агроцикла (хоким, менеджер); коротко' },
  kpiHaLand: { id: 'district.overview.kpi.haLand', defaultMessage: 'га земли', description: 'Единица у числа гектаров уникальной земли (не «га работ»); коротко' },
  kpiHa: { id: 'district.overview.kpi.ha', defaultMessage: 'га', description: 'Единица «гектары» у числа земли района; коротко' },
  kpiActiveNote: { id: 'district.overview.kpi.activeNote', defaultMessage: '{title} · этап: {stage}', description: 'Подпись KPI «Активный агроцикл»; {title} — «Хлопок 2026», {stage} — название этапа или «не назван»' },
  stageUnnamed: { id: 'district.overview.kpi.stageUnnamed', defaultMessage: 'не назван', description: 'Название этапа, когда источник его не назвал; встаёт после «этап:»; строчными' },
  kpiActiveLoading: { id: 'district.overview.kpi.activeLoading', defaultMessage: 'агроцикл ещё загружается', description: 'Подпись KPI «Активный агроцикл», пока данные грузятся; строчными' },
  kpiActiveNone: { id: 'district.overview.kpi.activeNone', defaultMessage: 'в срезе нет агроцикла со статусом «идёт»', description: 'Подпись KPI «Активный агроцикл», когда нет идущего агроцикла; строчными' },
  kpiLand: { id: 'district.overview.kpi.land', defaultMessage: 'Земля района', description: 'Название KPI: вся сельхозземля района; коротко' },
  kpiLandNote: { id: 'district.overview.kpi.landNote', defaultMessage: '{massifs} района', description: 'Подпись KPI «Земля района»; {massifs} — «5 массивов»' },
  kpiUnread: { id: 'district.overview.kpi.unread', defaultMessage: 'данные района не прочитаны — число не выдумывается', description: 'Подпись KPI, когда данные района не получены: число не подставляется; строчными' },
  kpiUnreadShort: { id: 'district.overview.kpi.unreadShort', defaultMessage: 'данные района не прочитаны', description: 'Короткая подпись KPI, когда данные района не получены; строчными' },
  kpiFarms: { id: 'district.overview.kpi.farms', defaultMessage: 'Хозяйства', description: 'Название KPI: число хозяйств района; коротко' },
  kpiFarmsNote: { id: 'district.overview.kpi.farmsNote', defaultMessage: 'в реестре среза', description: 'Подпись KPI «Хозяйства»: откуда число; строчными' },
  kpiContours: { id: 'district.overview.kpi.contours', defaultMessage: 'Контуры на карте', description: 'Название KPI: сколько контуров реестра нарисовано на карте; коротко' },
  kpiContoursNote: {
    id: 'district.overview.kpi.contoursNote',
    defaultMessage: 'из {count, plural, one {# контура} few {# контуров} many {# контуров} other {# контура}} реестра',
    description: 'Подпись KPI «Контуры на карте»: из скольких контуров реестра; {count} — всего контуров',
  },

  cardActive: { id: 'district.overview.card.active', defaultMessage: 'Активный агроцикл', description: 'Заголовок карточки идущего агроцикла на странице «Район»' },
  cardActiveAside: { id: 'district.overview.card.activeAside', defaultMessage: '{works} в агроцикле', description: 'Справа в заголовке карточки агроцикла; {works} — «40 работ»' },
  gateActive: { id: 'district.overview.gate.active', defaultMessage: 'карточка активного агроцикла', description: 'Что не загрузилось (строчными): подставляется в сообщение о том, что блок ждёт данные' },
  activeNoneTitle: { id: 'district.overview.active.noneTitle', defaultMessage: 'Активного агроцикла нет', description: 'Заголовок пустого состояния карточки идущего агроцикла' },
  activeNoneReason: { id: 'district.overview.active.noneReason', defaultMessage: 'в срезе не нашлось агроцикла со статусом «идёт»: либо сезон закрыт, либо следующий ещё не заведён', description: 'Пояснение в пустом состоянии «Активного агроцикла нет»; строчными' },
  cardClosed: { id: 'district.overview.card.closed', defaultMessage: 'Закрытые агроциклы', description: 'Заголовок карточки со списком закрытых агроциклов' },
  closedNoneTitle: { id: 'district.overview.closed.noneTitle', defaultMessage: 'Закрытых агроциклов нет', description: 'Заголовок пустого состояния карточки закрытых агроциклов' },
  closedNoneReadyReason: { id: 'district.overview.closed.noneReady', defaultMessage: 'в срезе только идущие агроциклы — истории пока не накопилось', description: 'Пояснение в пустом состоянии: список прочитан, закрытых нет; строчными' },
  closedNoneUnreadReason: { id: 'district.overview.closed.noneUnread', defaultMessage: 'список агроциклов не прочитан', description: 'Пояснение в пустом состоянии: список агроциклов не получен; строчными' },

  cardQueue: { id: 'district.overview.card.queue', defaultMessage: 'Очередь внимания', description: 'Заголовок карточки с очередью позиций, на которые стоит обратить внимание (хоким, менеджер)' },
  queueAside: { id: 'district.overview.queue.aside', defaultMessage: 'верхние {top} из {total}', description: 'Справа в заголовке очереди внимания; {top} — сколько показано, {total} — всего позиций' },
  gateQueue: { id: 'district.overview.gate.queue', defaultMessage: 'очередь внимания', description: 'Что не загрузилось (строчными): подставляется в сообщение о том, что блок ждёт данные' },
  queueEmptyTitle: { id: 'district.overview.queue.emptyTitle', defaultMessage: 'Очередь пуста', description: 'Заголовок пустого состояния очереди внимания' },
  queueEmptyReason: { id: 'district.overview.queue.emptyReason', defaultMessage: 'тестовый сервер не вернул ни одной позиции по этому срезу; это не значит, что вопросов нет — значит, ранжировать было нечего', description: 'Пояснение в пустом состоянии «Очередь пуста»; строчными' },
  queueNote: { id: 'district.overview.queue.note', defaultMessage: 'Порядок задан рангом тестового сервера. Клиент его не пересчитывает и своих сводных баллов не изобретает.', description: 'Пояснение под очередью внимания: порядок позиций не пересчитывается экраном' },
  queueRest: { id: 'district.overview.queue.rest', defaultMessage: 'Остальные позиции', description: 'Заголовок раскрывающегося блока с остальными позициями очереди' },
  queueRestSummary: {
    id: 'district.overview.queue.restSummary',
    defaultMessage: 'ещё {count, plural, one {# позиция} few {# позиции} many {# позиций} other {# позиции}}',
    description: 'Краткая подпись свёрнутого блока остальных позиций очереди; {count} — число позиций',
  },
  queueFoot: { id: 'district.overview.queue.foot', defaultMessage: 'Ранг {rank} · наблюдается с {date}', description: 'Мелкая подпись позиции очереди; {rank} — номер в очереди, {date} — полная дата, с какой наблюдается' },
  queueFootNoHref: { id: 'district.overview.queue.footNoHref', defaultMessage: 'Ранг {rank} · наблюдается с {date} · адреса у позиции нет', description: 'Мелкая подпись позиции очереди без ссылки; {rank} — номер, {date} — полная дата' },

  geoNone: { id: 'district.overview.geo.none', defaultMessage: 'Разбивку по происхождению геометрии показать нечем: данные контуров не прочитаны.', description: 'Пояснение справа от карты, когда нет данных о происхождении геометрии контуров' },
  geoCandidate: {
    id: 'district.overview.geo.candidate',
    defaultMessage:
      'Принятой геометрией закрыты {accepted} из {total, plural, one {# контура} few {# контуров} many {# контуров} other {# контура}}; ещё {candidate, plural, one {# показан как кандидат} few {# показаны как кандидаты} many {# показаны как кандидаты} other {# показаны как кандидаты}} — медный штриховой контур, сопоставление не подтверждено.',
    description: 'Пояснение справа от карты о происхождении геометрии; {accepted} — число контуров с принятой геометрией (строка), {total} — всего контуров, {candidate} — сколько показано как кандидаты',
  },
  geoAll: {
    id: 'district.overview.geo.all',
    defaultMessage: 'Все {count, plural, one {# контур идёт} few {# контура идут} many {# контуров идут} other {# контура идут}} с принятой геометрией.',
    description: 'Пояснение справа от карты: у всех контуров принятая геометрия; {count} — число контуров',
  },
  cropsMapEmpty: { id: 'district.overview.crops.mapEmpty', defaultMessage: 'Ни у одного контура среза не проставлена культура 2026: цвет полигонов сейчас говорит о пробеле в данных, а не о посеве.', description: 'Пояснение справа от карты, когда ни у одного контура нет культуры 2026' },

  discCrops: { id: 'district.overview.disc.crops', defaultMessage: 'Культуры 2026', description: 'Заголовок раскрывающегося блока с разбивкой площади по культурам' },
  discCropsSummary: { id: 'district.overview.disc.cropsSummary', defaultMessage: '{crops}, {ha} га размещено', description: 'Краткая подпись свёрнутого блока культур; {crops} — «5 культур», {ha} — гектары размещённой земли' },
  discUnread: { id: 'district.overview.disc.cropsUnread', defaultMessage: 'разбивка не прочитана', description: 'Краткая подпись свёрнутого блока культур, когда данные не получены; строчными' },
  gateCrops: { id: 'district.overview.gate.crops', defaultMessage: 'разбивка по культурам', description: 'Что не загрузилось (строчными): подставляется в сообщение о том, что блок ждёт данные' },
  cropsEmptyReason: { id: 'district.overview.crops.emptyReason', defaultMessage: 'ни у одного контура среза не проставлена культура 2026 — это пробел в данных, а не нулевой посев', description: 'Пояснение в пустой разбивке по культурам; строчными' },
  cropsUnplaced: {
    id: 'district.overview.crops.unplaced',
    defaultMessage: 'Ещё {contours} на {area} без культуры 2026. Это пробел в данных, а не «пар»: пар в справочнике — отдельная культура и считается строкой выше.',
    description: 'Пояснение под разбивкой по культурам; {contours} — «12 контуров», {area} — «340,50 га»',
  },
  discMassifs: { id: 'district.overview.disc.massifs', defaultMessage: 'Массивы', description: 'Заголовок раскрывающегося блока с таблицей массивов' },
  discMassifsSummary: { id: 'district.overview.disc.massifsSummary', defaultMessage: '{massifs}, {ha} га', description: 'Краткая подпись свёрнутого блока массивов; {massifs} — «5 массивов», {ha} — суммарная площадь' },
  discListUnread: { id: 'district.overview.disc.listUnread', defaultMessage: 'список не прочитан', description: 'Краткая подпись свёрнутого блока, когда список не получен; строчными' },
  gateMassifs: { id: 'district.overview.gate.massifs', defaultMessage: 'таблица массивов', description: 'Что не загрузилось (строчными): подставляется в сообщение о том, что блок ждёт данные' },
  massifsEmptyReason: { id: 'district.overview.massifs.emptyReason', defaultMessage: 'данные массивов прочитаны, но пусты — сравнивать нечего', description: 'Пояснение в пустой таблице массивов; строчными' },
  discReady: { id: 'district.overview.disc.ready', defaultMessage: 'Готовность данных', description: 'Заголовок раскрывающегося блока: какие данные есть и откуда они' },
  discReadySummary: { id: 'district.overview.disc.readySummary', defaultMessage: 'геометрия принята у {accepted} из {total}', description: 'Краткая подпись свёрнутого блока готовности данных; {accepted}/{total} — числа контуров' },
  discCountUnknown: { id: 'district.overview.disc.countUnknown', defaultMessage: 'счёт неизвестен', description: 'Краткая подпись свёрнутого блока, когда счёта нет; строчными' },

  markReal: { id: 'district.overview.mark.real', defaultMessage: 'Реальные исходники', description: 'Метка доверия у строки готовности данных: данные из реальных источников' },
  markGenerated: { id: 'district.overview.mark.generated', defaultMessage: 'Сгенерировано', description: 'Метка доверия у строки готовности данных: данные сгенерированы для демонстрации' },
  markCandidates: { id: 'district.overview.mark.candidates', defaultMessage: 'Часть — кандидаты', description: 'Метка доверия: часть геометрии контуров ещё кандидаты, не приняты' },
  markDerived: { id: 'district.overview.mark.derived', defaultMessage: 'Производное', description: 'Метка доверия: значение получено расчётом из других данных' },
  readyRegistry: { id: 'district.overview.ready.registry', defaultMessage: 'Реестр контуров и площади', description: 'Название строки в «Готовности данных»: реестр контуров' },
  readyRegistryFact: { id: 'district.overview.ready.registryFact', defaultMessage: '{contours} · {area} · {massifs}', description: 'Факты строки «Реестр контуров и площади»; {contours} — «300 контуров», {area} — «1 200,00 га», {massifs} — «5 массивов»' },
  readyRegistryUnread: { id: 'district.overview.ready.registryUnread', defaultMessage: 'данные района не прочитаны: {reason}', description: 'Факт строки готовности, когда данные района не получены; {reason} — причина' },
  readyGeometry: { id: 'district.overview.ready.geometry', defaultMessage: 'Геометрия контуров', description: 'Название строки в «Готовности данных»: геометрия контуров' },
  readyGeometryFact: { id: 'district.overview.ready.geometryFact', defaultMessage: 'принята у {accepted} из {total}; кандидатов {candidate}; без геометрии {none}', description: 'Факты строки «Геометрия контуров»; все значения — числа контуров' },
  readyGeometryUnread: { id: 'district.overview.ready.geometryUnread', defaultMessage: 'данные контуров не прочитаны: {reason}', description: 'Факт строки «Геометрия контуров», когда данные не получены; {reason} — причина' },
  readyFarms: { id: 'district.overview.ready.farms', defaultMessage: 'Хозяйства и их земля', description: 'Название строки в «Готовности данных»: хозяйства' },
  readyFarmsFact: { id: 'district.overview.ready.farmsFact', defaultMessage: '{farms} с направлением и главой', description: 'Факт строки «Хозяйства и их земля»; {farms} — «264 хозяйства»' },
  readyFarmsUnread: { id: 'district.overview.ready.farmsUnread', defaultMessage: 'счёт неизвестен: данные района не прочитаны', description: 'Факт строки «Хозяйства и их земля», когда данные не получены' },
  readyCycles: { id: 'district.overview.ready.cycles', defaultMessage: 'Агроциклы и работы', description: 'Название строки в «Готовности данных»: агроциклы и работы' },
  readyCyclesFact: {
    id: 'district.overview.ready.cyclesFact',
    defaultMessage:
      '{cycles}: {active, plural, one {# идёт} few {# идут} many {# идут} other {# идут}}, {closed, plural, one {# закрыт} few {# закрыты} many {# закрыты} other {# закрыты}}; в активном агроцикле {works}',
    description: 'Факты строки «Агроциклы и работы»; {cycles} — «2 агроцикла», {active}/{closed} — сколько идёт и закрыто, {works} — «40 работ» или «работы не прочитаны»',
  },
  readyWorksUnread: { id: 'district.overview.ready.worksUnread', defaultMessage: 'работы не прочитаны', description: 'Вместо числа работ активного агроцикла, когда работы не получены; строчными' },
  readyCyclesUnread: { id: 'district.overview.ready.cyclesUnread', defaultMessage: 'данные агроциклов не прочитаны: {reason}', description: 'Факт строки «Агроциклы и работы», когда данные не получены; {reason} — причина' },
  readyWater: { id: 'district.overview.ready.water', defaultMessage: 'Вода активного агроцикла', description: 'Название строки в «Готовности данных»: вода активного агроцикла' },
  readyWaterFact: { id: 'district.overview.ready.waterFact', defaultMessage: 'подано {delivered} из {requested}; охват {coverage}; заявок {requests}', description: 'Факты строки «Вода активного агроцикла»; {delivered}/{requested} — объёмы в м³, {coverage} — процент, {requests} — число заявок' },
  readyWaterUnread: { id: 'district.overview.ready.waterUnread', defaultMessage: 'данные воды не прочитаны: {reason}', description: 'Факт строки «Вода активного агроцикла», когда данные не получены; {reason} — причина' },
  waterNoCycle: { id: 'district.overview.ready.waterNoCycle', defaultMessage: 'агроцикл ещё не выбран', description: 'Причина по умолчанию, почему не прочитаны данные воды; строчными' },
  readyQuarantine: { id: 'district.overview.ready.quarantine', defaultMessage: 'Карантин', description: 'Название строки в «Готовности данных»: карантин' },
  readyQuarantineFact: { id: 'district.overview.ready.quarantineFact', defaultMessage: 'красных и жёлтых {contours}; обследований {inspections}; охват {coverage}', description: 'Факты строки «Карантин»; {contours} — «20 контуров», {inspections} — число обследований, {coverage} — процент' },
  readyQuarantineUnread: { id: 'district.overview.ready.quarantineUnread', defaultMessage: 'данные карантина не прочитаны: {reason}', description: 'Факт строки «Карантин», когда данные не получены; {reason} — причина' },
});

/* ---------------------------------------------------------- агрегаты */

interface CropBreakdown {
  placed: CropSlice[];
  placedArea: number;
  unplacedContours: number;
  unplacedArea: number;
}

function breakdownCrops(rows: ContourMeta[], crops: Crop[]): CropBreakdown {
  const byId = new Map<string, CropSlice & { contours: number }>();
  let unplacedContours = 0;
  let unplacedArea = 0;

  for (const row of rows) {
    if (!row.crop_2026) {
      unplacedContours += 1;
      unplacedArea += row.area_ha;
      continue;
    }
    const known = crops.find((c) => c.id === row.crop_2026);
    const slice = byId.get(row.crop_2026) ?? {
      id: row.crop_2026,
      name: known?.name ?? row.crop_2026,
      color: known?.color ?? 'line',
      contours: 0,
      area: 0,
    };
    slice.contours += 1;
    slice.area += row.area_ha;
    byId.set(row.crop_2026, slice);
  }

  const placed = [...byId.values()].sort((a, b) => b.area - a.area);
  return {
    placed,
    placedArea: placed.reduce((sum, s) => sum + s.area, 0),
    unplacedContours,
    unplacedArea,
  };
}

/* -------------------------------------------------- очередь внимания */

function attentionHref(item: AttentionItem): string | null {
  const { cycle_id, work_id, farm_id, contour_id } = item.ref;
  if (item.level === 'contour' && contour_id) return `/contours/${contour_id}`;
  if (item.level === 'farm' && farm_id) return `/farms/${farm_id}`;
  if (cycle_id && work_id) return `/cycles/${cycle_id}/works/${work_id}`;
  if (contour_id) return `/contours/${contour_id}`;
  if (farm_id) return `/farms/${farm_id}`;
  if (cycle_id) return `/cycles/${cycle_id}`;
  return null;
}

function queueView(item: AttentionItem): QueueItemView {
  const href = attentionHref(item);
  return {
    key: item.id,
    kind: item.kind,
    level: levelCaption(item.level),
    title: item.title,
    href,
    body: item.body,
    foot: tr(href ? m.queueFoot : m.queueFootNoHref, { rank: item.rank, date: dateLong(item.since) }),
  };
}

/* ------------------------------------------------------------- экран */

/**
 * Экран выбирает реализацию по источнику маршрута: канон — отдельный
 * компонент (`OverviewApi`), фикстуры — прежний, без изменений.
 */
export function Overview() {
  return useRouteSource()?.source === 'api' ? <OverviewApi /> : <OverviewFixtures />;
}

export function OverviewFixtures() {
  const t = useT();
  const meta = useTrackData<Meta>(FILES.meta);
  const cycles = useTrackData<Cycle[]>(FILES.cycles);
  const crops = useTrackData<Crop[]>(FILES.crops);
  const massifs = useTrackData<Massif[]>(FILES.massifs);
  const contours = useTrackData<ContourMeta[]>(FILES.contours);
  const attention = useTrackData<AttentionItem[]>(FILES.attention);
  const quarantine = useTrackData<QuarantineFile>(FILES.quarantine);

  const cycleList = useMemo(() => cycles.data ?? [], [cycles.data]);
  const active = cycleList.find((c) => c.status === 'active') ?? null;
  const closed = cycleList.filter((c) => c.status === 'closed');

  const works = useTrackData<Work[]>(active ? FILES.works(active.id) : null);
  const water = useTrackData<WaterFile>(active ? FILES.water(active.id) : null);

  const district = srv(meta.data?.district) ?? t(m.districtName);
  const asOf = meta.data?.as_of ?? null;
  const totals = meta.data?.totals ?? null;
  const isTrackB = meta.data?.track === 'B';

  usePageTrail([{ level: levelCaption('district'), name: district }]);

  /** Имя цикла — культура плюс сезон: «Хлопок 2026», а не голое «2026». */
  const titleOf = (cycle: Cycle): string => cycleTitle(cycle, crops.data);

  const geometry = useMemo(() => {
    const rows = contours.data ?? [];
    let accepted = 0;
    let candidate = 0;
    let none = 0;
    for (const row of rows) {
      if (row.geometry === 'accepted') accepted += 1;
      else if (row.geometry === 'candidate') candidate += 1;
      else none += 1;
    }
    return { total: rows.length, accepted, candidate, none };
  }, [contours.data]);

  const cropStats = useMemo(
    () => breakdownCrops(contours.data ?? [], crops.data ?? []),
    [contours.data, crops.data],
  );

  const massifRows = useMemo(
    () => [...(massifs.data ?? [])].sort((a, b) => b.area_ha - a.area_ha),
    [massifs.data],
  );
  const massifArea = massifRows.reduce((sum, m) => sum + m.area_ha, 0);

  const queue = useMemo(
    () => [...(attention.data ?? [])].sort((a, b) => a.rank - b.rank),
    [attention.data],
  );
  const queueTop = queue.slice(0, DENSITY.listItems);
  const queueRest = queue.slice(DENSITY.listItems);

  const activeWorks = works.data ?? [];

  return (
    <Stack>
      <PageHeader
        level="district"
        title={t(m.title, { district })}
        purpose={asOf ? t(m.purposeDate, { date: dateLong(asOf) }) : t(m.purposeNoDate)}
        actions={
          meta.data ? (
            <EvidenceMark
              kind={meta.source === 'stub' ? 'demo' : isTrackB ? 'derived' : 'demo'}
              label={
                meta.source === 'stub'
                  ? t(m.markStub)
                  : isTrackB
                    ? t(m.markTrackB)
                    : t(m.markTrackA)
              }
            />
          ) : null
        }
      />

      {/* ------------------------------------------------------- 4 числа */}
      <div className="ov-kpis">
        <KpiCard
          label={t(m.kpiActive)}
          value={active ? ha(active.area_ha) : '—'}
          unit={active ? t(m.kpiHaLand) : undefined}
          icon={Sprout}
          variant="hero"
          note={
            active
              ? t(m.kpiActiveNote, { title: titleOf(active), stage: active.stage_now ?? t(m.stageUnnamed) })
              : cycles.state === 'loading'
                ? t(m.kpiActiveLoading)
                : t(m.kpiActiveNone)
          }
        />
        <KpiCard
          label={t(m.kpiLand)}
          value={totals ? ha(totals.agri_area_ha) : '—'}
          unit={totals ? t(m.kpiHa) : undefined}
          icon={LandPlot}
          note={
            totals
              ? t(m.kpiLandNote, { massifs: countOf(totals.massifs, WORDS.massif) })
              : t(m.kpiUnread)
          }
        />
        <KpiCard
          label={t(m.kpiFarms)}
          value={totals ? num(totals.farms) : '—'}
          icon={Building2}
          note={totals ? t(m.kpiFarmsNote) : t(m.kpiUnreadShort)}
        />
        <KpiCard
          label={t(m.kpiContours)}
          value={totals ? num(totals.mapped_contours) : '—'}
          icon={MapPin}
          note={
            totals
              ? t(m.kpiContoursNote, { count: totals.contours })
              : t(m.kpiUnreadShort)
          }
        />
      </div>

      {/* ------------------------------------- цикл + очередь внимания */}
      <Grid cols="main-rail">
        <Stack tight>
          <Card
            size="lg"
            title={t(m.cardActive)}
            aside={
              activeWorks.length > 0 ? t(m.cardActiveAside, { works: countOf(activeWorks.length, WORDS.work) }) : null
            }
          >
            <DataGate res={cycles} what={t(m.gateActive)}>
              {() =>
                active ? (
                  <ActiveCycle
                    cycle={active}
                    title={titleOf(active)}
                    asOf={asOf}
                    worksRes={works}
                  />
                ) : (
                  <EmptyState
                    compact
                    icon={Sprout}
                    title={t(m.activeNoneTitle)}
                    reason={t(m.activeNoneReason)}
                  />
                )
              }
            </DataGate>
          </Card>

          <Card
            tone="soft"
            title={t(m.cardClosed)}
            aside={closed.length > 0 ? countOf(closed.length, WORDS.cycle) : null}
          >
            {closed.length === 0 ? (
              <EmptyState
                compact
                title={t(m.closedNoneTitle)}
                reason={
                  cycles.state === 'ready'
                    ? t(m.closedNoneReadyReason)
                    : t(m.closedNoneUnreadReason)
                }
              />
            ) : (
              <Stack tight>
                {closed.map((cycle) => (
                  <ClosedCycleRow
                    key={cycle.id}
                    cycleId={cycle.id}
                    title={titleOf(cycle)}
                    facts={closedCycleFacts(cycle)}
                  />
                ))}
              </Stack>
            )}
          </Card>
        </Stack>

        <Card
          title={t(m.cardQueue)}
          aside={
            queue.length > 0
              ? t(m.queueAside, { top: num(queueTop.length), total: num(queue.length) })
              : null
          }
        >
          <DataGate res={attention} what={t(m.gateQueue)}>
            {() =>
              queue.length === 0 ? (
                <EmptyState
                  compact
                  icon={ListChecks}
                  title={t(m.queueEmptyTitle)}
                  reason={t(m.queueEmptyReason)}
                />
              ) : (
                <Stack tight>
                  <ul className="ov-queue">
                    {queueTop.map((item) => (
                      <QueueItem item={queueView(item)} key={item.id} />
                    ))}
                  </ul>
                  <div className="ov-note">{t(m.queueNote)}</div>
                  {queueRest.length > 0 ? (
                    <Disclosure
                      label={t(m.queueRest)}
                      summary={t(m.queueRestSummary, { count: queueRest.length })}
                    >
                      <ul className="ov-queue">
                        {queueRest.map((item) => (
                          <QueueItem item={queueView(item)} key={item.id} />
                        ))}
                      </ul>
                    </Disclosure>
                  ) : null}
                </Stack>
              )
            }
          </DataGate>
        </Card>
      </Grid>

      {/* ----------------------------------------------------- кадр карты */}
      <MapFrameCard
        crops={crops.data ?? undefined}
        massifs={massifs.data ?? undefined}
        geometryNote={
          geometry.total === 0
            ? t(m.geoNone)
            : geometry.candidate > 0
              ? t(m.geoCandidate, { accepted: num(geometry.accepted), total: geometry.total, candidate: geometry.candidate })
              : t(m.geoAll, { count: geometry.total })
        }
        cropSlices={cropStats.placed}
        cropsEmptyNote={t(m.cropsMapEmpty)}
      />

      {/* --------------------------------------------------- раскрытия */}
      <Stack tight>
        <Disclosure
          label={t(m.discCrops)}
          summary={
            contours.state === 'ready'
              ? t(m.discCropsSummary, { crops: countOf(cropStats.placed.length, WORDS.crop), ha: ha(cropStats.placedArea) })
              : t(m.discUnread)
          }
        >
          <DataGate res={contours} what={t(m.gateCrops)}>
            {() => (
              <CropsBreakdown
                placed={cropStats.placed}
                emptyReason={t(m.cropsEmptyReason)}
                unplacedNote={
                  cropStats.unplacedContours > 0
                    ? t(m.cropsUnplaced, {
                      contours: countOf(cropStats.unplacedContours, WORDS.contour),
                      area: haUnit(cropStats.unplacedArea),
                    })
                    : null
                }
              />
            )}
          </DataGate>
        </Disclosure>

        <Disclosure
          label={t(m.discMassifs)}
          summary={
            massifRows.length > 0
              ? t(m.discMassifsSummary, { massifs: countOf(massifRows.length, WORDS.massif), ha: ha(massifArea) })
              : t(m.discListUnread)
          }
        >
          <DataGate res={massifs} what={t(m.gateMassifs)}>
            {() => (
              <MassifTable
                rows={massifRows.map((row) => ({
                  id: row.id,
                  name: row.name,
                  contours: row.contours,
                  farms: row.farms,
                  area: row.area_ha,
                }))}
                emptyReason={t(m.massifsEmptyReason)}
              />
            )}
          </DataGate>
        </Disclosure>

        <Disclosure
          label={t(m.discReady)}
          summary={
            geometry.total > 0
              ? t(m.discReadySummary, { accepted: num(geometry.accepted), total: num(geometry.total) })
              : t(m.discCountUnknown)
          }
        >
          <div className="ov-ready">
            <ReadyRow
              name={t(m.readyRegistry)}
              fact={
                totals
                  ? t(m.readyRegistryFact, {
                    contours: countOf(totals.contours, WORDS.contour),
                    area: haUnit(totals.agri_area_ha),
                    massifs: countOf(totals.massifs, WORDS.massif),
                  })
                  : t(m.readyRegistryUnread, { reason: meta.reason ?? t(COMMON.noReason) })
              }
              mark={
                totals ? (
                  <EvidenceMark
                    kind={isTrackB ? 'fact' : 'demo'}
                    label={isTrackB ? t(m.markReal) : t(m.markGenerated)}
                  />
                ) : null
              }
            />

            <ReadyRow
              name={t(m.readyGeometry)}
              fact={
                geometry.total > 0
                  ? t(m.readyGeometryFact, {
                    accepted: num(geometry.accepted),
                    total: num(geometry.total),
                    candidate: num(geometry.candidate),
                    none: num(geometry.none),
                  })
                  : t(m.readyGeometryUnread, { reason: contours.reason ?? t(COMMON.noReason) })
              }
              mark={
                geometry.total > 0 ? (
                  <EvidenceMark
                    kind={geometry.candidate > 0 ? 'candidate' : 'derived'}
                    label={geometry.candidate > 0 ? t(m.markCandidates) : t(m.markDerived)}
                  />
                ) : null
              }
            />

            <ReadyRow
              name={t(m.readyFarms)}
              fact={
                totals
                  ? t(m.readyFarmsFact, { farms: countOf(totals.farms, WORDS.farm) })
                  : t(m.readyFarmsUnread)
              }
              mark={<EvidenceMark kind="demo" label={t(m.markGenerated)} />}
            />

            <ReadyRow
              name={t(m.readyCycles)}
              fact={
                cycleList.length > 0
                  ? t(m.readyCyclesFact, {
                    cycles: countOf(cycleList.length, WORDS.cycle),
                    active: cycleList.filter((c) => c.status === 'active').length,
                    closed: closed.length,
                    works: activeWorks.length > 0 ? countOf(activeWorks.length, WORDS.work) : t(m.readyWorksUnread),
                  })
                  : t(m.readyCyclesUnread, { reason: cycles.reason ?? t(COMMON.noReason) })
              }
              mark={<EvidenceMark kind="demo" label={t(m.markGenerated)} />}
            />

            <ReadyRow
              name={t(m.readyWater)}
              fact={
                water.data
                  ? t(m.readyWaterFact, {
                    delivered: m3(water.data.summary.delivered_m3),
                    requested: m3(water.data.summary.requested_m3),
                    coverage: pct(water.data.summary.coverage_pct, 1),
                    requests: num(water.data.summary.requests),
                  })
                  : t(m.readyWaterUnread, { reason: water.reason ?? t(m.waterNoCycle) })
              }
              mark={water.data ? <EvidenceMark kind="demo" label={t(m.markGenerated)} /> : null}
            />

            <ReadyRow
              name={t(m.readyQuarantine)}
              fact={
                quarantine.data
                  ? t(m.readyQuarantineFact, {
                    contours: countOf(quarantine.data.summary.contours_watch, WORDS.contour),
                    inspections: num(quarantine.data.summary.inspections),
                    coverage: pct(quarantine.data.summary.coverage_pct, 1),
                  })
                  : t(m.readyQuarantineUnread, { reason: quarantine.reason ?? t(COMMON.noReason) })
              }
              mark={quarantine.data ? <EvidenceMark kind="demo" label={t(m.markGenerated)} /> : null}
            />
          </div>
        </Disclosure>
      </Stack>
    </Stack>
  );
}
